export const ATTRIBUTION_STORAGE_KEY = "vunora_first_touch_v1";
export const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1_000;

const ATTRIBUTION_FIELDS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "gbraid",
  "wbraid",
] as const;

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface LandingAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  referrer?: string;
  landing_path?: string;
}

interface AttributionEnvelope {
  version: 1;
  capturedAt: number;
  expiresAt: number;
  data: LandingAttribution;
}

interface CaptureOptions {
  storage: StorageLike;
  now: number;
  search: string;
  path: string;
  referrer: string;
}

export type MessageAngle = "forecast" | "bills" | "salary" | "family";

export interface MessageMatch {
  angle: MessageAngle;
  support: string;
  outcome: string;
}

const MESSAGE_MATCH_COPY: Record<MessageAngle, Omit<MessageMatch, "angle">> = {
  forecast: {
    support: "Vunora turns your expense history into a clear range for the month ahead.",
    outcome: "See the likely month before it begins.",
  },
  bills: {
    support: "Vunora brings upcoming rent, card bills, and regular expenses into one forward view.",
    outcome: "Know the large bills before they arrive.",
  },
  salary: {
    support: "Vunora shows what your next salary cycle may need to cover before the month fills up.",
    outcome: "Plan the salary month with more context.",
  },
  family: {
    support: "Vunora helps households see recurring and changing expenses before the month gets tight.",
    outcome: "Bring the next month into view early.",
  },
};

function clean(value: string | null, maxLength = 500): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed.slice(0, maxLength) : undefined;
}

function readStored(storage: StorageLike, now: number): LandingAttribution | null {
  try {
    const raw = storage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const envelope = JSON.parse(raw) as AttributionEnvelope;
    if (envelope.version !== 1 || envelope.expiresAt <= now || !envelope.data) {
      storage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return null;
    }
    return envelope.data;
  } catch {
    return null;
  }
}

export function captureFirstTouch(options: CaptureOptions): LandingAttribution {
  const stored = readStored(options.storage, options.now);
  if (stored) return stored;

  const params = new URLSearchParams(options.search);
  const data: LandingAttribution = {};
  for (const field of ATTRIBUTION_FIELDS) {
    const value = clean(params.get(field));
    if (value) data[field] = value;
  }

  const referrer = clean(options.referrer, 2_000);
  const landingPath = clean(options.path, 2_000);
  if (referrer) data.referrer = referrer;
  if (landingPath) data.landing_path = landingPath;

  const envelope: AttributionEnvelope = {
    version: 1,
    capturedAt: options.now,
    expiresAt: options.now + ATTRIBUTION_TTL_MS,
    data,
  };
  try {
    options.storage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(envelope));
  } catch {
    // Attribution storage is helpful, never required for the waitlist.
  }
  return data;
}

export function resolveMessageMatch(search: string): MessageMatch | null {
  const params = new URLSearchParams(search);
  const haystack = [
    params.get("angle"),
    params.get("utm_content"),
    params.get("utm_campaign"),
    params.get("utm_term"),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const angle = (Object.keys(MESSAGE_MATCH_COPY) as MessageAngle[]).find((key) =>
    new RegExp(`(^|[^a-z])${key}([^a-z]|$)`).test(haystack),
  );
  return angle ? { angle, ...MESSAGE_MATCH_COPY[angle] } : null;
}
